import { z } from 'zod';

const optionalTrimmed = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const senderDetailsSchema = z
  .object({
    company_name: optionalTrimmed,
    first_name: z.string().trim().min(1, 'Required'),
    last_name: z.string().trim().min(1, 'Required'),
    email: z.string().trim().email('Invalid email'),
    phone_prefix: z
      .string()
      .trim()
      .regex(/^\+\d{1,4}$/u, 'Invalid prefix (e.g. +48)'),
    phone_number: z.string().trim().min(3, 'Invalid phone'),
    street: z.string().trim().min(1, 'Required'),
    house_number: z.string().trim().min(1, 'Required'),
    flat_number: optionalTrimmed,
    city: z.string().trim().min(1, 'Required'),
    postal_code: z.string().trim().min(1, 'Required'),
    country_code: z.string().length(2, 'Must be a 2-letter country code'),
    subdivision_code: optionalTrimmed,
  })
  // InPost needs the GB region (point-origin subdivisionCode + customs-rule resolution).
  .superRefine((sender, ctx) => {
    if (sender.country_code === 'GB' && !sender.subdivision_code) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['subdivision_code'],
        message: 'Required',
      });
    }
  });

// Only sender details are form-managed on the General tab; the other general
// settings (shipping defaults, delivery estimate) save instantly per control.
export const senderSettingsSchema = z.object({
  sender_details: senderDetailsSchema,
});

export type SenderSettingsFormValues = z.infer<typeof senderSettingsSchema>;

export const pickupLocationSchema = z.object({
  name: z.string().trim().min(1, 'Required'),
  street: z.string().trim().min(1, 'Required'),
  house_number: z.string().trim().min(1, 'Required'),
  flat_number: optionalTrimmed,
  // One-time pickups are Poland-only - postal codes use the PL XX-XXX format.
  postal_code: z
    .string()
    .trim()
    .regex(/^\d{2}-\d{3}$/u, 'Invalid postal code (e.g. 00-001)'),
  city: z.string().trim().min(1, 'Required'),
  description: optionalTrimmed,
  is_default: z.boolean().optional(),
});

export type PickupLocationFormValues = z.infer<typeof pickupLocationSchema>;

export const contactPersonSchema = z.object({
  first_name: z.string().trim().min(1, 'Required'),
  last_name: z.string().trim().min(1, 'Required'),
  email: z.string().trim().email('Invalid email'),
  phone_prefix: z
    .string()
    .trim()
    .regex(/^\+\d{1,4}$/u, 'Invalid prefix (e.g. +48)'),
  phone_number: z
    .string()
    .trim()
    .regex(/^\d{9}$/u, 'Invalid phone (9 digits)'),
  is_default: z.boolean().optional(),
});

export type ContactPersonFormValues = z.infer<typeof contactPersonSchema>;

// Mirrors the backend caps (`carrier.limits.custom_package_*`): values are
// cm/kg, positive, with typo-guard maximums - the carrier's physical limits
// are enforced at shipment creation.
export const customPackageSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(255, 'Too long'),
  length: z.number().min(0.01, 'Must be at least 0.01').max(1000, 'Too large'),
  width: z.number().min(0.01, 'Must be at least 0.01').max(1000, 'Too large'),
  height: z.number().min(0.01, 'Must be at least 0.01').max(1000, 'Too large'),
  weight: z.number().min(0.01, 'Must be at least 0.01').max(1000, 'Too large'),
});

export type CustomPackageFormValues = z.infer<typeof customPackageSchema>;

export const createPickupOrderSchema = z.object({
  pickup_location_id: z.string().min(1, 'Required'),
  contact_person_id: z.string().min(1, 'Required'),
  pickup_date: z.string().min(1, 'Required'),
  hour_from: z.string().min(1, 'Required'),
  hour_to: z.string().min(1, 'Required'),
  count: z.number().int().min(1, 'Must be at least 1'),
  weight: z.number().min(0.1, 'Must be at least 0.1'),
});

export type CreatePickupOrderFormValues = z.infer<typeof createPickupOrderSchema>;
